import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { sitesApi, imagePromptsApi, type Widget, type SiteSettings, type Site } from '../api/client';

export default function SiteDetail() {
  const { siteId } = useParams<{ siteId: string }>();

  const { data: siteData, isLoading: loadingSite } = useQuery({
    queryKey: ['site', siteId],
    queryFn: () => sitesApi.get(siteId!),
    enabled: !!siteId,
  });

  const { data: widgetsData, isLoading: loadingWidgets } = useQuery({
    queryKey: ['site-widgets', siteId],
    queryFn: () => sitesApi.getWidgets(siteId!),
    enabled: !!siteId,
  });

  const site = siteData?.site;
  const widgets = widgetsData?.widgets || [];

  if (loadingSite) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  if (!site) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 mb-4">Site not found</p>
        <Link to="/sites" className="btn btn-primary">
          Back to Sites
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-8">
        <Link to="/sites" className="text-gray-400 hover:text-gray-600">
          ← Back
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">{site.domain}</h1>
      </div>

      {/* Site Configuration */}
      <SiteConfiguration siteId={siteId!} site={site} />

      {/* Branding Settings */}
      <BrandingSettings siteId={siteId!} settings={site.settings} />

      {/* Image Prompt Settings */}
      <ImagePromptSettings siteId={siteId!} settings={site.settings} />

      {/* Widgets */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Widgets</h2>
          <Link to={`/sites/${siteId}/widgets`} className="btn btn-primary">
            View All Widgets ({widgets.length})
          </Link>
        </div>

        {loadingWidgets ? (
          <div className="text-center py-8 text-gray-500">Loading widgets...</div>
        ) : widgets.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No widgets generated yet. Use the WordPress plugin or API to create widgets.
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-4">
              Showing {Math.min(5, widgets.length)} most recent widgets
            </p>
            <div className="divide-y divide-gray-100">
              {widgets.slice(0, 5).map((widget) => (
                <WidgetRow key={widget.id} widget={widget} siteId={siteId!} />
              ))}
            </div>
            {widgets.length > 5 && (
              <Link
                to={`/sites/${siteId}/widgets`}
                className="block text-center py-3 text-blue-600 hover:text-blue-800 text-sm mt-2"
              >
                View all {widgets.length} widgets →
              </Link>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SiteConfiguration({ siteId, site }: { siteId: string; site: Site }) {
  const queryClient = useQueryClient();
  const [domain, setDomain] = useState(site.domain);
  const [defaultWidgetType, setDefaultWidgetType] = useState<'auto' | 'quiz' | 'poll'>(
    site.settings.defaultWidgetType || 'auto'
  );
  const [autoGenerate, setAutoGenerate] = useState(
    site.settings.autoGenerate !== false
  );

  const updateMutation = useMutation({
    mutationFn: (data: { domain?: string; settings?: Partial<SiteSettings> }) =>
      sitesApi.update(siteId, {
        domain: data.domain,
        settings: data.settings ? { ...site.settings, ...data.settings } : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site', siteId] });
    },
  });

  function handleSave() {
    updateMutation.mutate({
      domain: domain !== site.domain ? domain : undefined,
      settings: {
        defaultWidgetType,
        autoGenerate,
      },
    });
  }

  const hasChanges =
    domain !== site.domain ||
    defaultWidgetType !== (site.settings.defaultWidgetType || 'auto') ||
    autoGenerate !== (site.settings.autoGenerate !== false);

  return (
    <div className="card mb-8">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Site Configuration</h2>

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Domain
          </label>
          <input
            type="text"
            className="input"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="example.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            WordPress Connection
          </label>
          <p className="font-medium py-2">
            {site.has_wp_password ? (
              <span className="text-green-600">✓ Connected as {site.wp_username}</span>
            ) : (
              <span className="text-gray-400">Not configured</span>
            )}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Default Widget Type
          </label>
          <select
            className="input"
            value={defaultWidgetType}
            onChange={(e) => setDefaultWidgetType(e.target.value as 'auto' | 'quiz' | 'poll')}
          >
            <option value="auto">Auto (AI picks best)</option>
            <option value="quiz">Quiz</option>
            <option value="poll">Poll</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Auto lets AI choose the best format. Quiz or Poll forces that type.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Auto-Generate Widgets
          </label>
          <label className="flex items-center gap-2 py-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              checked={autoGenerate}
              onChange={(e) => setAutoGenerate(e.target.checked)}
            />
            <span className="text-sm text-gray-700">
              Automatically generate widgets when posts are published
            </span>
          </label>
        </div>
      </div>

      <button
        className="btn btn-primary"
        onClick={handleSave}
        disabled={updateMutation.isPending || !hasChanges}
      >
        {updateMutation.isPending ? 'Saving...' : 'Save Configuration'}
      </button>

      {updateMutation.isSuccess && (
        <span className="ml-3 text-green-600 text-sm">Saved!</span>
      )}
      {updateMutation.isError && (
        <span className="ml-3 text-red-600 text-sm">
          Error: {updateMutation.error.message}
        </span>
      )}
    </div>
  );
}

function BrandingSettings({ siteId, settings }: { siteId: string; settings: SiteSettings }) {
  const queryClient = useQueryClient();
  const [brandName, setBrandName] = useState(settings.brandName || '');
  const [ogBackgroundColor, setOgBackgroundColor] = useState(settings.ogBackgroundColor || '#1a1a2e');
  const [ogTextColor, setOgTextColor] = useState(settings.ogTextColor || '#ffffff');
  const [logoKey, setLogoKey] = useState(Date.now()); // For forcing logo refresh

  const updateMutation = useMutation({
    mutationFn: (newSettings: Partial<SiteSettings>) =>
      sitesApi.update(siteId, { settings: { ...settings, ...newSettings } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site', siteId] });
    },
  });

  const uploadLogoMutation = useMutation({
    mutationFn: (file: File) => sitesApi.uploadLogo(siteId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site', siteId] });
      setLogoKey(Date.now()); // Force logo image refresh
    },
  });

  const deleteLogoMutation = useMutation({
    mutationFn: () => sitesApi.deleteLogo(siteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site', siteId] });
    },
  });

  function handleSave() {
    updateMutation.mutate({
      brandName: brandName || undefined,
      ogBackgroundColor,
      ogTextColor,
    });
  }

  function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      uploadLogoMutation.mutate(file);
    }
  }

  return (
    <div className="card mb-8">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Share Image Branding</h2>
      <p className="text-sm text-gray-500 mb-4">
        Customize how shared quiz/poll results appear on social media. The logo and brand name will be incorporated into AI-generated share images.
      </p>

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Brand Name
          </label>
          <input
            type="text"
            className="input"
            placeholder="Your Site Name"
            value={brandName}
            onChange={(e) => setBrandName(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Logo
          </label>
          {settings.hasLogo ? (
            <div className="flex items-center gap-3">
              <img
                src={`${sitesApi.getLogoUrl(siteId)}?t=${logoKey}`}
                alt="Logo"
                className="h-10 max-w-[120px] object-contain bg-gray-100 rounded p-1"
              />
              <button
                className="btn btn-secondary text-sm"
                onClick={() => deleteLogoMutation.mutate()}
                disabled={deleteLogoMutation.isPending}
              >
                {deleteLogoMutation.isPending ? 'Removing...' : 'Remove'}
              </button>
              <label className="btn btn-secondary text-sm cursor-pointer">
                Replace
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleLogoUpload}
                />
              </label>
            </div>
          ) : (
            <label className="flex items-center justify-center w-full h-20 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-colors">
              <div className="text-center">
                <p className="text-sm text-gray-600">
                  {uploadLogoMutation.isPending ? 'Uploading...' : 'Click to upload logo'}
                </p>
                <p className="text-xs text-gray-400">PNG, JPG, SVG</p>
              </div>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleLogoUpload}
                disabled={uploadLogoMutation.isPending}
              />
            </label>
          )}
          {uploadLogoMutation.isError && (
            <p className="text-red-600 text-sm mt-1">{uploadLogoMutation.error.message}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Background Color (hint for AI)
          </label>
          <div className="flex gap-2">
            <input
              type="color"
              className="h-10 w-14 rounded border cursor-pointer"
              value={ogBackgroundColor}
              onChange={(e) => setOgBackgroundColor(e.target.value)}
            />
            <input
              type="text"
              className="input flex-1"
              value={ogBackgroundColor}
              onChange={(e) => setOgBackgroundColor(e.target.value)}
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Text Color (hint for AI)
          </label>
          <div className="flex gap-2">
            <input
              type="color"
              className="h-10 w-14 rounded border cursor-pointer"
              value={ogTextColor}
              onChange={(e) => setOgTextColor(e.target.value)}
            />
            <input
              type="text"
              className="input flex-1"
              value={ogTextColor}
              onChange={(e) => setOgTextColor(e.target.value)}
            />
          </div>
        </div>
      </div>

      <button
        className="btn btn-primary"
        onClick={handleSave}
        disabled={updateMutation.isPending}
      >
        {updateMutation.isPending ? 'Saving...' : 'Save Branding'}
      </button>

      {updateMutation.isSuccess && (
        <span className="ml-3 text-green-600 text-sm">Saved!</span>
      )}
      {updateMutation.isError && (
        <span className="ml-3 text-red-600 text-sm">
          Error: {updateMutation.error.message}
        </span>
      )}
    </div>
  );
}

function ImagePromptSettings({ siteId, settings }: { siteId: string; settings: SiteSettings }) {
  const queryClient = useQueryClient();
  const [quizPrompt, setQuizPrompt] = useState(settings.imagePrompts?.quiz || '');
  const [pollPrompt, setPollPrompt] = useState(settings.imagePrompts?.poll || '');
  const [activeTab, setActiveTab] = useState<'quiz' | 'poll'>('quiz');
  const [showDefaultModal, setShowDefaultModal] = useState(false);

  // Fetch defaults from API (single source of truth)
  const { data: defaults } = useQuery({
    queryKey: ['image-prompt-defaults'],
    queryFn: imagePromptsApi.getDefaults,
    staleTime: Infinity, // These don't change during a session
  });

  const updateMutation = useMutation({
    mutationFn: (imagePrompts: { quiz?: string; poll?: string }) =>
      sitesApi.update(siteId, { settings: { ...settings, imagePrompts } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site', siteId] });
    },
  });

  function handleSave() {
    updateMutation.mutate({
      quiz: quizPrompt || undefined,
      poll: pollPrompt || undefined,
    });
  }

  function handleUseDefault() {
    if (!defaults) return;
    if (activeTab === 'quiz') {
      setQuizPrompt(defaults.prompts.quiz);
    } else {
      setPollPrompt(defaults.prompts.poll);
    }
    setShowDefaultModal(false);
  }

  const hasChanges =
    quizPrompt !== (settings.imagePrompts?.quiz || '') ||
    pollPrompt !== (settings.imagePrompts?.poll || '');

  const activePlaceholders = defaults?.placeholders[activeTab] || [];
  const activeDefaultPrompt = defaults?.prompts[activeTab] || '';

  return (
    <div className="card mb-8">
      <h2 className="text-lg font-semibold text-gray-900 mb-2">Image Generation Prompts</h2>
      <p className="text-sm text-gray-500 mb-4">
        Customize the AI prompts used to generate share images. Leave blank to use defaults.
        Use placeholders like <code className="bg-gray-100 px-1 rounded">{'{{title}}'}</code> that get replaced with actual content.
      </p>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <button
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'quiz'
              ? 'bg-blue-100 text-blue-700'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          onClick={() => setActiveTab('quiz')}
        >
          Quiz Images
        </button>
        <button
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'poll'
              ? 'bg-blue-100 text-blue-700'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          onClick={() => setActiveTab('poll')}
        >
          Poll Images
        </button>
      </div>

      {/* Prompt Editor */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-gray-700">
            {activeTab === 'quiz' ? 'Quiz' : 'Poll'} Image Prompt
          </label>
          <button
            className="text-sm text-blue-600 hover:text-blue-800"
            onClick={() => setShowDefaultModal(true)}
          >
            View Default Prompt
          </button>
        </div>
        <textarea
          className="input font-mono text-sm"
          rows={10}
          placeholder={`Leave blank for default ${activeTab} prompt...`}
          value={activeTab === 'quiz' ? quizPrompt : pollPrompt}
          onChange={(e) =>
            activeTab === 'quiz'
              ? setQuizPrompt(e.target.value)
              : setPollPrompt(e.target.value)
          }
        />
      </div>

      {/* Available Placeholders */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
        <p className="text-sm font-medium text-gray-700 mb-2">Available Placeholders:</p>
        <div className="grid grid-cols-2 gap-2 text-sm">
          {activePlaceholders.map((p) => (
            <div key={p.placeholder} className="flex gap-2">
              <code className="bg-white px-1 rounded border text-blue-600">{p.placeholder}</code>
              <span className="text-gray-500">{p.description}</span>
            </div>
          ))}
        </div>
      </div>

      <button
        className="btn btn-primary"
        onClick={handleSave}
        disabled={updateMutation.isPending || !hasChanges}
      >
        {updateMutation.isPending ? 'Saving...' : 'Save Prompts'}
      </button>

      {updateMutation.isSuccess && (
        <span className="ml-3 text-green-600 text-sm">Saved!</span>
      )}
      {updateMutation.isError && (
        <span className="ml-3 text-red-600 text-sm">
          Error: {updateMutation.error.message}
        </span>
      )}

      {/* Default Prompt Modal */}
      {showDefaultModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowDefaultModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                Default {activeTab === 'quiz' ? 'Quiz' : 'Poll'} Prompt
              </h3>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={() => setShowDefaultModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="p-4 overflow-auto flex-1">
              <pre className="whitespace-pre-wrap font-mono text-sm bg-gray-50 p-4 rounded-lg border">
                {activeDefaultPrompt}
              </pre>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDefaultModal(false)}
              >
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUseDefault}
              >
                Copy to Editor
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function WidgetRow({ widget, siteId }: { widget: Widget; siteId: string }) {
  const statusColors = {
    active: 'bg-green-100 text-green-700',
    draft: 'bg-yellow-100 text-yellow-700',
    archived: 'bg-gray-100 text-gray-700',
  };

  let content: { hook_title?: string } = {};
  try {
    content = JSON.parse(widget.content);
  } catch {}

  return (
    <div className="flex items-center justify-between py-4">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-3">
          <span className="text-xl">{widget.type === 'quiz' ? '❓' : '📊'}</span>
          <div className="min-w-0">
            <p className="font-medium text-gray-900 truncate">
              {content.hook_title || 'Untitled Widget'}
            </p>
            <p className="text-sm text-gray-500 truncate">{widget.source_url}</p>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[widget.status]}`}
        >
          {widget.status}
        </span>
        <span className="text-sm text-gray-500">
          {new Date(widget.created_at).toLocaleDateString()}
        </span>
        <Link
          to={`/sites/${siteId}/widgets/${widget.id}`}
          className="btn btn-secondary text-sm"
        >
          Edit
        </Link>
      </div>
    </div>
  );
}
